// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import type { ChargeItem, ChargeItemDefinition, Patient } from '@medplum/fhirtypes';
import { isSelfPayPatient, resolveBillingPathway } from './extensions';

export type PayerClass = 'insurance' | 'self-pay';
type PropertyGroup = NonNullable<ChargeItemDefinition['propertyGroup']>[number];

function getPropertyGroupPayerClass(group: PropertyGroup): PayerClass | undefined {
  const identifiers =
    group.applicability?.flatMap((applicability) => [applicability.id, applicability.description]) ?? [];
  if (identifiers.includes('insurance')) {
    return 'insurance';
  }
  if (identifiers.includes('self-pay')) {
    return 'self-pay';
  }
  return undefined;
}

export function resolvePayerClass(patient: Patient, dateOfService: string): PayerClass {
  const pathway = resolveBillingPathway(patient, dateOfService);
  if (pathway === 'INSURANCE') {
    return 'insurance';
  }
  if (pathway === 'SELF-PAY') {
    return 'self-pay';
  }
  return isSelfPayPatient(patient) ? 'self-pay' : 'insurance';
}

export function resolvePrice(definition: ChargeItemDefinition, payerClass: PayerClass): number {
  const groups = definition.propertyGroup ?? [];
  const group =
    groups.find((candidate) => getPropertyGroupPayerClass(candidate) === payerClass) ??
    (payerClass === 'insurance' && groups.length === 1 && !groups[0].applicability ? groups[0] : undefined);
  const amount = group?.priceComponent?.find((component) => component.type === 'base')?.amount?.value;
  if (typeof amount !== 'number' || amount <= 0) {
    const code = definition.code?.coding?.[0]?.code ?? definition.url.split('/').pop() ?? 'service';
    throw new Error(`Pricing not configured for ${code}`);
  }
  return amount;
}

function getReferenceId(reference: string | undefined, resourceType: string): string | undefined {
  const prefix = `${resourceType}/`;
  return reference?.startsWith(prefix) ? reference.slice(prefix.length) : undefined;
}

async function readPatient(medplum: MedplumClient, chargeItem: ChargeItem): Promise<Patient> {
  const patientId = getReferenceId(chargeItem.subject?.reference, 'Patient');
  if (!patientId) {
    throw new Error('ChargeItem subject must reference a Patient');
  }
  return medplum.readResource('Patient', patientId);
}

async function resolveDateOfService(medplum: MedplumClient, chargeItem: ChargeItem): Promise<string> {
  const occurrence = chargeItem.occurrenceDateTime ?? chargeItem.occurrencePeriod?.start;
  if (occurrence) {
    return occurrence;
  }
  const encounterId = getReferenceId(chargeItem.context?.reference, 'Encounter');
  if (encounterId) {
    const encounter = await medplum.readResource('Encounter', encounterId);
    if (encounter.period?.start) {
      return encounter.period.start;
    }
  }
  throw new Error(`Date of service not configured for ChargeItem ${chargeItem.id ?? 'unknown'}`);
}

/** Resolve and persist payer-specific ChargeItem pricing. */
export async function applyChargeItemDefinition(medplum: MedplumClient, chargeItem: ChargeItem): Promise<ChargeItem> {
  if (!chargeItem.id || !chargeItem.definitionCanonical?.[0]) {
    return chargeItem;
  }

  const matches = await medplum.searchResources('ChargeItemDefinition', `url=${chargeItem.definitionCanonical[0]}`);
  const definition = matches[0];
  if (!definition) {
    return chargeItem;
  }

  const [patient, dateOfService] = await Promise.all([
    readPatient(medplum, chargeItem),
    resolveDateOfService(medplum, chargeItem),
  ]);
  const price = resolvePrice(definition, resolvePayerClass(patient, dateOfService));
  return medplum.updateResource({
    ...chargeItem,
    priceOverride: { value: price, currency: 'USD' },
  });
}
